<script setup>
/* Zusammenführen: Quellverein wählen (kommt von der Zeile), Zielverein wählen, Vorschau mit Zahlen, bestätigen.
 *
 * Folge der Funktion merge_football_clubs (supabase/klubregister-3-funktionen.sql), nur admin, eine Transaktion:
 * alle Spieler und Transfers der Quelle zeigen danach auf das Ziel, die Quelle wird aus dem Register gelöscht.
 * Die Logo-Datei der Quelle bleibt im Speicher liegen. Hat das Ziel kein Logo, bleibt an den umgehängten Zeilen
 * der bisherige Logo-Text stehen (Festlegung 7b).
 */
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import OverlayModal from './OverlayModal.vue';
import ClubLogo from './ClubLogo.vue';
import ClubSelect from './ClubSelect.vue';
import { useFootballClubs } from '../composables/useFootballClubs.js';
import { useSnackbar } from '../composables/useSnackbar.js';
import { useConfirm } from '../composables/useConfirm.js';

const props = defineProps({
  source: { type: Object, required: true },
});
const emit = defineEmits(['close']);

const { t, te } = useI18n();
const { show } = useSnackbar();
const { confirm } = useConfirm();
const { byId, usage, mergeInto } = useFootballClubs();

const targetId = ref(null);
const busy = ref(false);

const target = computed(() => (targetId.value ? byId.value.get(targetId.value) || null : null));
const sourceUsage = computed(() => usage.value.get(props.source.id) || { players: 0, transfers: 0 });

async function submit() {
  if (!target.value || busy.value) return;
  const accepted = await confirm({
    title: t('clubRegister.mergeTitle'),
    message: t('clubRegister.mergeConfirm', { source: props.source.name, target: target.value.name }),
  });
  if (!accepted) return;
  busy.value = true;
  try {
    await mergeInto(props.source.id, target.value.id);
    show(t('clubRegister.merged', { source: props.source.name, target: target.value.name }));
    emit('close');
  } catch (e) {
    show(te(`errors.${e.message}`) ? t(`errors.${e.message}`) : t('errors.saveFailed'), 'error');
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <OverlayModal :title="$t('clubRegister.mergeTitle')" :width="520" :z="60" @close="emit('close')">
    <p class="cm-from">
      <span class="cm-label">{{ $t('clubRegister.mergeFrom') }}</span>
      <ClubLogo :path="source.logoPath" :size="28" />
      <strong>{{ source.name }}</strong>
    </p>

    <ClubSelect
      v-model="targetId"
      :label="$t('clubRegister.mergeInto')"
      :clearable="false"
      :exclude="source.id"
    />

    <p v-if="target" class="cm-preview" role="status">
      {{ $t('clubRegister.mergePreview', { players: sourceUsage.players, transfers: sourceUsage.transfers, source: source.name, target: target.name }) }}
    </p>
    <p class="cm-note">{{ $t('clubRegister.mergeNote') }}</p>

    <template #actions>
      <button class="btn ghost small" :disabled="busy" @click="emit('close')">{{ $t('common.cancel') }}</button>
      <button class="btn small" :disabled="!target || busy" @click="submit">
        {{ busy ? $t('common.loading') : $t('clubRegister.mergeAction') }}
      </button>
    </template>
  </OverlayModal>
</template>

<style scoped>
.cm-from { display: flex; align-items: center; gap: 10px; margin: 0 0 14px; }
.cm-label { font-size: var(--fs-2xs); color: var(--dim); text-transform: uppercase; }
.cm-preview { margin: 14px 0 0; padding: 10px 12px; border: 1px solid var(--amber); border-radius: 8px; font-size: var(--fs-sm); color: var(--text); }
.cm-note { margin: 10px 0 0; font-size: var(--fs-xs); color: var(--muted); }
</style>
